import { da, type Messages } from './messages/da';

/**
 * Supported UI languages. Adding one: create `messages/<code>.ts` exporting a `Messages` object
 * and register it here; TypeScript then flags every missing or mistyped message.
 */
const LOCALES = {
	da: { messages: da, intlTag: 'da' }
} satisfies Record<string, { messages: Messages; intlTag: string }>;

export type Locale = keyof typeof LOCALES;

const DEFAULT_LOCALE: Locale = 'da';
const STORAGE_KEY = 'madplan-locale';

/** The UI language is a per-device preference, like the colour theme. */
class I18nState {
	locale = $state<Locale>(readStoredLocale());
	/** The current language's messages; reading it in markup re-renders on a language change. */
	t: Messages = $derived(LOCALES[this.locale].messages);
	collator = $derived(new Intl.Collator(LOCALES[this.locale].intlTag));
	/** Dates like "25. sep. 2026". */
	dateFormat = $derived(
		new Intl.DateTimeFormat(LOCALES[this.locale].intlTag, { dateStyle: 'medium' })
	);

	set(locale: Locale) {
		this.locale = locale;
		try {
			localStorage.setItem(STORAGE_KEY, locale);
		} catch {
			// Private mode or blocked storage: the language still applies for this session.
		}
		applyLocale(locale);
	}
}

function isLocale(value: string | null): value is Locale {
	return value !== null && Object.hasOwn(LOCALES, value);
}

function readStoredLocale(): Locale {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		return isLocale(stored) ? stored : DEFAULT_LOCALE;
	} catch {
		return DEFAULT_LOCALE;
	}
}

export function applyLocale(locale: Locale) {
	document.documentElement.lang = locale;
}

export const i18n = new I18nState();

/** Sorts names the way the current language does (æ, ø, å after z in Danish). */
export function compareText(a: string, b: string): number {
	return i18n.collator.compare(a, b);
}
